import { v4 as uuidv4 } from 'uuid';
import { AUDIO_PLAY_COMMAND } from '../stores/audioStore.js';
import { parseSchemaCommand } from '../actions/commandParser.js';
import { serializeCommandAction } from '../actions/commandSerializer.js';
import { parseSetObjectPropertyCommand } from '../actions/actionPropertyParser.js';
import { resolveActionProperty, resolveActionPropertyByWireKey } from '../actions/actionPropertyResolver.js';
import { serializeSetObjectPropertyAction } from '../actions/actionPropertySerializer.js';
import { serializeRuntimePropertyValue } from '../actions/propertyValueCodec.js';
import {
    getH3dLightProperty,
    parseH3dLightCommand,
    serializeH3dLightAction,
} from '../actions/model3dLightWireFormat.js';
import {
    serializeLegacyTemplateVisibilityAction,
    serializeSemanticTemplateVisibilityAction,
    validateTemplateVisibilityAction,
} from '../actions/templateVisibility.js';
import {
    GPIO_ENTITY_ACTION_TYPE,
    gpioCommandFirmwareFeatures,
    gpioEntityActionForm,
    gpioEntityActionToBinding,
    gpioEntityActionToCommand,
    migrateGpioEventBindings,
    validateGpioEntityAction,
} from '../actions/gpioEntityAction.js';
import { isLogicFlowEntry, serializeLogicFlowEntry } from '../actions/logicFlowWire.js';

export const ACTION_CAPABILITIES = [
    { id: 'command', label: 'Command', supportsValueToken: true },
    { id: 'templateVisibility:show', label: 'Template → Show' },
    { id: 'templateVisibility:hide', label: 'Template → Hide' },
    {
        id: 'setObjectProperty',
        label: 'Set object property',
        supportsValueToken: true,
    },
    { id: 'model3dLight', label: '3D Light', supportsValueToken: true },
    { id: GPIO_ENTITY_ACTION_TYPE, label: 'GPIO Entity', supportsValueToken: true },
];

export function createAction(type = 'command') {
    if (type === 'templateVisibility:show' || type === 'templateVisibility:hide') {
        return {
            id: uuidv4(),
            type: 'templateVisibility',
            operation: type.endsWith(':show') ? 'show' : 'hide',
            target: null,
        };
    }
    if (type === 'setObjectProperty') {
        return { id: uuidv4(), type, target: null, property: null, value: '' };
    }
    if (type === 'model3dLight') {
        return { id: uuidv4(), type, target: null, light: '', property: 'intensity', value: '' };
    }
    if (type === GPIO_ENTITY_ACTION_TYPE) {
        return {
            id: uuidv4(), type, entity: '', property: '', valueSource: 'event',
            sourceProperty: '', inMin: 0, inMax: 1, outMin: 0, outMax: 1, fixedValue: false,
        };
    }
    return { id: uuidv4(), type: 'command', commandId: '', parameters: {} };
}

export function parseAction(value, resolveTarget) {
    if (value && typeof value === 'object' && value.type) {
        if (value.type === 'setObjectText') {
            const target = resolveTarget?.(value.target?.pageId, value.target?.objectId);
            const property = resolveActionPropertyByWireKey(target, 'text');
            if (property) {
                return {
                    id: value.id || uuidv4(),
                    type: 'setObjectProperty',
                    target: value.target,
                    property: { ...property.property },
                    value: value.text ?? '',
                };
            }
            const pageId = Number(value.target?.pageId);
            const objectId = Number(value.target?.objectId);
            return {
                id: value.id || uuidv4(),
                type: 'command',
                commandId: '',
                parameters: {},
                command: Number.isInteger(pageId) && Number.isInteger(objectId)
                    ? `p${pageId}b${objectId}.text=${value.text ?? ''}`
                    : '',
            };
        }
        if (value.type === 'customCommand') {
            value = { ...value, type: 'command', commandId: '', parameters: {} };
        }
        if (value.type !== 'command' || value.commandId) return { ...value, id: value.id || uuidv4() };
    }

    const command = typeof value === 'string' ? value : value?.command;
    if (!command) return null;
    const templateVisibility = /^template\.(show|hide)\s*=\s*(\d+)$/i.exec(String(command).trim());
    if (templateVisibility) {
        return {
            id: value?.id || uuidv4(),
            type: 'templateVisibility',
            operation: templateVisibility[1].toLowerCase(),
            target: { templateId: Number(templateVisibility[2]) },
        };
    }
    const lightAction = parseH3dLightCommand(command);
    if (lightAction) return { id: value?.id || uuidv4(), ...lightAction };
    const propertyAction = parseSetObjectPropertyCommand(command, resolveTarget);
    if (propertyAction) return { id: value?.id || uuidv4(), ...propertyAction };
    const parsed = parseSchemaCommand(command);
    const action = parsed
        ? { id: value?.id || uuidv4(), type: 'command', ...parsed }
        : { id: value?.id || uuidv4(), type: 'command', commandId: '', parameters: {}, command };
    if (value?.resourceId) action.resourceId = value.resourceId;
    return action;
}

export function normalizeObjectActions(config = {}, resolveTarget) {
    const source = config.actions ?? config.action ?? {};
    let normalized;
    if (Array.isArray(source)) {
        normalized = source.map(handler => ({
            id: handler.id || uuidv4(),
            event: handler.event || '',
            actions: (handler.actions ?? []).map(entry => parseAction(entry, resolveTarget)).filter(Boolean),
        }));
    } else {
        normalized = [];
        for (const [event, entries] of Object.entries(source ?? {})) {
            const list = Array.isArray(entries) ? entries : [entries];
            const actions = list.map(entry => parseAction(entry, resolveTarget)).filter(Boolean);
            if (actions.length) normalized.push({ id: uuidv4(), event, actions });
        }
    }
    return migrateGpioEventBindings(normalized, config.gpio_bindings);
}

export function serializeAction(action, resolveTarget, context = {}) {
    if (action?.type === 'templateVisibility') {
        if (context.templateVisibilityMode === 'semantic') {
            return serializeSemanticTemplateVisibilityAction(action, context.resolveTemplateTarget);
        }
        return serializeLegacyTemplateVisibilityAction(action, context.resolveTemplateTarget, {
            ...context,
            normalizeObjectActions,
            serializeObjectActions,
        });
    }
    if (action?.type === 'setObjectProperty') {
        return serializeSetObjectPropertyAction(action, resolveTarget);
    }
    if (action?.type === 'model3dLight') return serializeH3dLightAction(action);
    if (action?.type === 'command') return serializeCommandAction(action);
    return null;
}

/**
 * Serialize ordered handlers to the firmware `action` map. GPIO Entity actions
 * the firmware can bind on the source object (`options.sourceNode`) come back
 * as `gpioBindings.events`; the others become entity commands. Conditions and
 * switches of the Logic view become objects in the command list, and their
 * GPIO Entity actions are always commands. `firmwareFeatures` lists what the
 * result needs beyond current firmware.
 */
export function serializeObjectActions(actions, options = {}) {
    const { resolveAudio, resolveTarget } = options;
    const wire = {};
    const errors = [];
    const gpioEvents = [];
    const firmwareFeatures = new Set();
    const serializeEntries = (entries, event, nested) => {
        const commands = [];
        for (const action of entries ?? []) {
            if (isLogicFlowEntry(action)) {
                const flow = serializeLogicFlowEntry(action, branch => serializeEntries(branch, event, true));
                if (flow.error) errors.push({ event, actionId: action.id, message: flow.error });
                else if (flow.value) {
                    commands.push(flow.value);
                    firmwareFeatures.add('conditions');
                }
                continue;
            }
            if (action?.type === GPIO_ENTITY_ACTION_TYPE &&
                gpioEntityActionForm(action, { event, node: options.sourceNode, nested }) === 'binding') {
                const binding = gpioEntityActionToBinding(action, {
                    event,
                    node: options.sourceNode,
                    entity: options.metadata?.entities?.find(entity => entity?.id === action.entity),
                });
                if (!binding) {
                    errors.push({ event, actionId: action.id, message: 'GPIO entity action is incomplete' });
                } else {
                    gpioEvents.push(binding);
                }
                continue;
            }
            if (action?.type === GPIO_ENTITY_ACTION_TYPE) {
                const command = gpioEntityActionToCommand(action);
                if (!command) errors.push({ event, actionId: action.id, message: 'GPIO entity action is incomplete' });
                else {
                    commands.push(command);
                    gpioCommandFirmwareFeatures(command).forEach(feature => firmwareFeatures.add(feature));
                }
                continue;
            }
            let command = serializeAction(action, resolveTarget, options);
            if (action?.type === 'templateVisibility' && !command) {
                errors.push({
                    event,
                    actionId: action.id,
                    message: validateTemplateVisibilityAction(
                        action, options.resolveTemplateTarget, options.sourcePageId,
                        { legacy: options.templateVisibilityMode !== 'semantic' },
                    ),
                });
            }
            if (command === AUDIO_PLAY_COMMAND) {
                command = resolveAudio?.(action.resourceId) ?? null;
                if (!command) errors.push({ event, actionId: action.id, message: 'Audio resource is missing' });
            }
            if (Array.isArray(command)) commands.push(...command.filter(Boolean));
            else if (command) commands.push(command);
        }
        return commands;
    };
    for (const handler of actions ?? []) {
        const event = handler.event;
        if (!event) {
            errors.push({ handlerId: handler.id, message: 'Event is required' });
            continue;
        }
        const commands = serializeEntries(handler.actions, event, false);
        if (commands.length) {
            if (wire[event]) {
                errors.push({ handlerId: handler.id, event, message: 'Duplicate event handler' });
                wire[event].push(...commands);
            } else {
                wire[event] = commands;
            }
        }
    }
    const result = { action: wire, errors };
    if (gpioEvents.length) result.gpioBindings = { events: gpioEvents };
    if (firmwareFeatures.size) result.firmwareFeatures = [...firmwareFeatures];
    return result;
}

/**
 * Split a serialized `action` map by firmware attribute: gesture events (a
 * schema's `gestures`, e.g. swipe_left → `swipe.left`) move to their own
 * attribute map; every other event stays in `action`.
 */
export function splitGestureWire(action, gestures = {}) {
    const result = { action: {}, attributes: {} };
    for (const [event, commands] of Object.entries(action ?? {})) {
        const wire = gestures?.[event]?.wire;
        if (wire) (result.attributes[wire.attribute] ??= {})[wire.key] = commands;
        else result.action[event] = commands;
    }
    return result;
}

export function validateHandler(handler, supportedEvents, handlers) {
    if (!handler?.event) return 'Event is required';
    if (!supportedEvents.includes(handler.event)) return 'Event is not supported by this object';
    if ((handlers ?? []).filter(item => item.event === handler.event).length > 1) return 'This event already has a handler';
    return '';
}

export function validateAction(action, resolveTarget, resolveTemplateTarget, sourcePageId, context = {}) {
    if (action?.type === 'templateVisibility') {
        return validateTemplateVisibilityAction(action, resolveTemplateTarget, sourcePageId);
    }
    if (action?.type === 'command') {
        if (!action.commandId) return action.command?.trim()
            ? 'Legacy command is not recognized by the current command schema'
            : 'Select a command';
        return serializeCommandAction(action) ? '' : 'Command parameters are incomplete';
    }
    if (action?.type === 'model3dLight') {
        const pageId = Number(action.target?.pageId);
        const objectId = Number(action.target?.objectId);
        if (!Number.isInteger(pageId) || !Number.isInteger(objectId)) return 'Select a 3D object';
        const target = resolveTarget?.(pageId, objectId);
        if (!target) return 'Target no longer exists';
        if (target.obj !== '3d') return 'Target is not a 3D object';
        if (!action.light) return 'Select a light';
        if (!getH3dLightProperty(action.property)) return 'Property is not supported';
        return serializeH3dLightAction(action) ? '' : 'Value is invalid for this property';
    }
    if (action?.type === GPIO_ENTITY_ACTION_TYPE) {
        return validateGpioEntityAction(action, context);
    }
    if (action?.type !== 'setObjectProperty') return 'Unsupported action type';
    const pageId = Number(action.target?.pageId);
    const objectId = Number(action.target?.objectId);
    if (!Number.isInteger(pageId) || !Number.isInteger(objectId)) return 'Select a target';
    const target = resolveTarget?.(pageId, objectId);
    if (!target) return 'Target no longer exists';
    const property = resolveActionProperty(target, action.property);
    if (!property) return 'Property is not available for actions';
    if (action.value === undefined) return 'Value is required';
    return serializeRuntimePropertyValue(action.value, property.metadata) === null
        ? 'Value is invalid for this property' : '';
}

export function hasConfiguredActions(handlers) {
    return Array.isArray(handlers) &&
        handlers.some(handler => Array.isArray(handler?.actions) && handler.actions.length > 0);
}
